import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addNotification } from '../../store/slices/uiSlice';
import AttendanceModal from './AttendanceModal';
import { adminApiService } from '../../services/adminApi';
import { requireAttendanceAccess } from '../common/SecureAdminPage';
import * as XLSX from 'xlsx';

const AttendancePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const toYmd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentWeekRange = () => {
    const today = new Date();
    const day = today.getDay(); // Sunday = 0, Monday = 1
    const diffToMonday = (day + 6) % 7;

    const monday = new Date(today);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(today.getDate() - diffToMonday);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return {
      start: toYmd(monday),
      end: toYmd(sunday)
    };
  };

  const defaultWeek = getCurrentWeekRange();

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  
  // Tab state for event type
  const [activeTab, setActiveTab] = useState('worship');
  
  // Date filter state - separate input values from applied filter values
  const [startDateInput, setStartDateInput] = useState(defaultWeek.start);
  const [endDateInput, setEndDateInput] = useState(defaultWeek.end);
  const [startDate, setStartDate] = useState(defaultWeek.start);
  const [endDate, setEndDate] = useState(defaultWeek.end);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    event_id: null,
    section_id: null
  });
  const [events, setEvents] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    console.log('🔄 AttendancePage: useEffect triggered - fetching events and attendance');
    console.log('🔍 AttendancePage: Current filters:', filters);
    console.log('🔍 AttendancePage: Current page:', currentPage, 'Page size:', pageSize);
    console.log('🔍 AttendancePage: Search term:', searchTerm);
    console.log('🔍 AttendancePage: Active tab:', activeTab);
    console.log('🔍 AttendancePage: Date range:', startDate, '-', endDate);
    fetchEvents();
    fetchAttendanceRecords();
  }, [currentPage, pageSize, searchTerm, filters, activeTab, startDate, endDate]);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventDetails(selectedEvent.id);
    } else {
      setSections([]);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      console.log('🔄 AttendancePage: Fetching events...');
      const response = await adminApiService.events.getAll({ skip: 0, limit: 1000 });
      console.log('✅ AttendancePage: Events API response:', response);
      console.log('✅ AttendancePage: Events response.data:', response.data);
      console.log('✅ AttendancePage: Events Array.isArray(response):', Array.isArray(response));
      console.log('✅ AttendancePage: Events Array.isArray(response.data):', Array.isArray(response.data));
      
      // Events API returns array directly, not wrapped in .data
      const eventsData = Array.isArray(response) ? response : (response.data || []);
      console.log('✅ AttendancePage: Final events data:', eventsData);
      setEvents(eventsData);
    } catch (error) {
      console.error('❌ AttendancePage: Failed to fetch events:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch events'
      }));
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      console.log('🔄 AttendancePage: Fetching event details for ID:', eventId);
      const response = await adminApiService.events.getById(eventId);
      console.log('✅ AttendancePage: Event details response:', response);
      console.log('✅ AttendancePage: Event sections:', response.sections);
      setSections(response.sections || []);
    } catch (error) {
      console.error('❌ AttendancePage: Failed to fetch event details:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch event details'
      }));
    }
  };

  const fetchAttendanceRecords = async () => {
    console.log('🔄 AttendancePage: fetchAttendanceRecords called');
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 AttendancePage: Fetching attendance records...');
      const params = new URLSearchParams({
        start: ((currentPage - 1) * pageSize).toString(),
        length: pageSize.toString(),
        search_value: searchTerm || '',
        order_column: '0', // Sort by ID
        order_dir: 'desc'
      });

      // Add event type filter (tab)
      if (activeTab) {
        console.log('🔍 AttendancePage: Adding event_type filter:', activeTab);
        params.append('event_type', activeTab);
      }

      // Add date filters
      if (startDate) {
        console.log('🔍 AttendancePage: Adding start_date filter:', startDate);
        params.append('start_date', startDate);
      }
      if (endDate) {
        console.log('🔍 AttendancePage: Adding end_date filter:', endDate);
        params.append('end_date', endDate);
      }

      // Add filters if selected
      if (filters.event_id) {
        console.log('🔍 AttendancePage: Adding event_id filter:', filters.event_id);
        params.append('event_id', filters.event_id.toString());
      }
      if (filters.section_id) {
        console.log('🔍 AttendancePage: Adding section_id filter:', filters.section_id);
        params.append('section_id', filters.section_id.toString());
      }

      console.log('🔍 AttendancePage: Final API params:', Object.fromEntries(params));
      const response = await adminApiService.attendance.getRecords(Object.fromEntries(params));
      
      console.log('✅ AttendancePage: Attendance records fetched:', response.data);
      console.log('✅ AttendancePage: Total records:', response.total);
      
      setAttendanceRecords(response.data || []);
      setTotalRecords(response.total || 0);
    } catch (error) {
      console.error('❌ AttendancePage: Failed to fetch attendance records:', error);
      setError('Failed to fetch attendance records');
      setAttendanceRecords([]);
      setTotalRecords(0);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch attendance records'
      }));
    } finally {
      setLoading(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalRecords / pageSize);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, activeTab, startDate, endDate]);

  // Reset event/section filters when tab changes
  useEffect(() => {
    setSelectedEvent(null);
    setFilters({ event_id: null, section_id: null });
    setSections([]);
  }, [activeTab]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleCreateAttendance = () => {
    setSelectedAttendance(null);
    setIsCreateMode(true);
    setShowAttendanceModal(true);
  };

  const handleEditAttendance = (attendance) => {
    setSelectedAttendance(attendance);
    setIsCreateMode(false);
    setShowAttendanceModal(true);
  };

  const handleCloseModal = () => {
    setSelectedAttendance(null);
    setShowAttendanceModal(false);
  };

  const handleModalSuccess = () => {
    fetchAttendanceRecords(); // Refresh the data
  };

  const handleDeleteAttendance = async (attendance) => {
    if (window.confirm(t('attendance.confirmDelete', { id: attendance.worship_id }))) {
      try {
        await adminApiService.attendance.deleteRecord(attendance.id);
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('attendance.notifications.deleteSuccess')
        }));
        fetchAttendanceRecords(); // Refresh data
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          title: t('common.errorTitle'),
          message: error.response?.data?.detail || t('attendance.notifications.deleteError')
        }));
      }
    }
  };

  const handleEventFilterChange = (e) => {
    console.log('🔄 AttendancePage: Event filter changed:', e.target.value);
    const eventId = e.target.value ? parseInt(e.target.value) : null;
    const event = events.find(ev => ev.id === eventId);
    
    console.log('🔍 AttendancePage: Selected event:', event);
    console.log('🔍 AttendancePage: Event ID:', eventId);
    
    setSelectedEvent(event || null);
    setFilters(prev => ({ 
      ...prev, 
      event_id: eventId,
      section_id: null // Reset section when event changes
    }));
    console.log('🔍 AttendancePage: Updated filters with event_id:', eventId);
  };

  const handleSectionFilterChange = (e) => {
    console.log('🔄 AttendancePage: Section filter changed:', e.target.value);
    const sectionId = e.target.value ? parseInt(e.target.value) : null;
    console.log('🔍 AttendancePage: Section ID:', sectionId);
    
    setFilters(prev => ({ ...prev, section_id: sectionId }));
    console.log('🔍 AttendancePage: Updated filters with section_id:', sectionId);
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({
        start: '0',
        length: '10000',
        order_column: '0',
        order_dir: 'desc'
      });

      if (activeTab) params.append('event_type', activeTab);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filters.event_id) params.append('event_id', String(filters.event_id));
      if (filters.section_id) params.append('section_id', String(filters.section_id));

      const response = await adminApiService.attendance.getRecords(Object.fromEntries(params));
      const rows = (response.data || []).map((record, idx) => ({
        [t('attendance.table.worshipId')]: (record.worship_id !== null && record.worship_id !== undefined && record.worship_id !== '') ? record.worship_id : '-',
        [t('attendance.table.mobilePhone')]: record.mobile || '-',
        [t('attendance.table.memberName')]: record.member_name || '-',
        [t('attendance.table.event')]: record.event_name || '-',
        [t('attendance.table.section')]: record.section_name || '-',
        [t('attendance.table.qrTime')]: formatQRDateTime(record.current_datetime),
        [t('attendance.table.checkInTime')]: formatDateTime(record.check_in_time)
      }));

      if (rows.length === 0) {
        dispatch(addNotification({ type: 'warning', message: t('attendance.noRecords') }));
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [12, 14, 18, 22, 16, 18, 20].map(w => ({ wch: w }));

      const tabNameMap = {
        worship: t('attendance.tabs.worship'),
        course: t('attendance.tabs.course'),
        event: t('attendance.tabs.event'),
        cell_group: t('attendance.tabs.cellGroup'),
        meeting: t('attendance.tabs.meeting')
      };

      const sheetName = `${tabNameMap[activeTab] || 'Attendance'}`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const datePart = new Date().toISOString().split('T')[0];
      const eventPart = selectedEvent?.event_name ? `_${selectedEvent.event_name}` : '';
      const sectionPart = filters.section_id ? `_${sections.find(s => s.id === filters.section_id)?.section_name || filters.section_id}` : '';
      const rangePart = (startDate || endDate) ? `_${startDate || ''}${(startDate || endDate) ? 'to' : ''}${endDate || ''}` : '';
      const filename = `Attendance${eventPart}${sectionPart}${rangePart}_${datePart}.xlsx`;

      XLSX.writeFile(wb, filename);
      dispatch(addNotification({ type: 'success', message: '已匯出出席記錄' }));
    } catch (err) {
      console.error('Export attendance failed', err);
      dispatch(addNotification({ type: 'error', message: t('attendance.error') }));
    }
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format QR datetime
  const formatQRDateTime = (qrDateTime) => {
    if (!qrDateTime || qrDateTime.length !== 12) return qrDateTime;
    
    const year = qrDateTime.slice(0, 4);
    const month = qrDateTime.slice(4, 6);
    const day = qrDateTime.slice(6, 8);
    const hour = qrDateTime.slice(8, 10);
    const minute = qrDateTime.slice(10, 12);
    
    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  if (loading && attendanceRecords.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && attendanceRecords.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{t('attendance.error')}</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Debug Info */}
      <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200">
        <div className="text-sm text-yellow-800">
          <strong>Debug Info:</strong> Loading: {loading.toString()}, Error: {error || 'none'}, 
          AttendanceRecords: {attendanceRecords.length}, Events: {events.length}, 
          Sections: {sections.length}, TotalRecords: {totalRecords}, Tab: {activeTab}
        </div>
      </div>

      {/* Security Notice */}
      <div className="px-6 py-4 bg-green-50 border-b border-green-200">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-800 text-sm font-medium">
            🔒 {t('attendance.secureNotice')}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex -mb-px min-w-max" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('worship')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'worship'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {t('attendance.tabs.worship')}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('course')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'course'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {t('attendance.tabs.course')}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('event')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'event'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('attendance.tabs.event')}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('cell_group')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'cell_group'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('attendance.tabs.cellGroup')}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('meeting')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'meeting'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t('attendance.tabs.meeting')}
            </span>
          </button>
        </nav>
      </div>

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h3 className="text-lg font-medium text-gray-900">
            {activeTab === 'worship' ? t('attendance.header.regularWorship') : 
             activeTab === 'course' ? t('attendance.header.course') : 
             activeTab === 'cell_group' ? t('attendance.header.cellGroup') :
             activeTab === 'meeting' ? t('attendance.header.meeting') : t('attendance.header.events')} {t('attendance.header.attendanceRecords')}
          </h3>
          <div className="flex items-center space-x-4 flex-wrap gap-y-2">
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <label className="text-sm text-gray-500 mr-2">{t('attendance.filters.from')}</label>
                <input
                  type="date"
                  value={startDateInput}
                  onChange={(e) => setStartDateInput(e.target.value)}
                  onBlur={(e) => setStartDate(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setStartDate(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="flex items-center">
                <label className="text-sm text-gray-500 mr-2">{t('attendance.filters.to')}</label>
                <input
                  type="date"
                  value={endDateInput}
                  onChange={(e) => setEndDateInput(e.target.value)}
                  onBlur={(e) => setEndDate(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setEndDate(e.target.value); }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              {(startDate || endDate) && (
                <button
                  onClick={() => { setStartDate(''); setEndDate(''); setStartDateInput(''); setEndDateInput(''); }}
                  className="px-2 py-2 text-gray-500 hover:text-gray-700 text-sm"
                  title={t('attendance.filters.clearDateFilter')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={5}>{t('attendance.filters.perPage', { count: 5 })}</option>
                <option value={10}>{t('attendance.filters.perPage', { count: 10 })}</option>
                <option value={25}>{t('attendance.filters.perPage', { count: 25 })}</option>
                <option value={50}>{t('attendance.filters.perPage', { count: 50 })}</option>
              </select>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder={`Search ${activeTab === 'worship' ? 'worship' : activeTab === 'course' ? 'course' : activeTab === 'cell_group' ? 'cell group' : activeTab === 'meeting' ? 'meeting' : 'event'} attendance...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={handleCreateAttendance}
              className={`${
                activeTab === 'worship' ? 'bg-blue-600 hover:bg-blue-700' : 
                activeTab === 'course' ? 'bg-green-600 hover:bg-green-700' : 
                activeTab === 'cell_group' ? 'bg-orange-600 hover:bg-orange-700' :
                activeTab === 'meeting' ? 'bg-teal-600 hover:bg-teal-700' :
                'bg-purple-600 hover:bg-purple-700'
              } text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('attendance.actions.createRecord')}
            </button>
            <button
              onClick={handleExportExcel}
              className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v16h16V4H4zm4 4h8m-8 4h8m-8 4h8" />
              </svg>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('attendance.filters.selectEvent')}
            </label>
            <select
              value={filters.event_id || ''}
              onChange={handleEventFilterChange}
              className="block w-full border border-gray-300 rounded-md pl-3 pr-8 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All {activeTab === 'worship' ? t('attendance.tabs.worship') : activeTab === 'course' ? t('attendance.tabs.course') : activeTab === 'cell_group' ? t('attendance.tabs.cellGroup') : activeTab === 'meeting' ? t('attendance.tabs.meeting') : t('attendance.tabs.event')}</option>
              {events
                .filter(event => (event.event_type || 'event') === activeTab)
                .map(event => (
                <option key={event.id} value={event.id}>
                  {event.event_name}
                  {event.venue && ` (${event.venue})`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('attendance.filters.selectSection')}
            </label>
            <select
              value={filters.section_id || ''}
              onChange={handleSectionFilterChange}
              disabled={!selectedEvent}
              className="block w-full border border-gray-300 rounded-md pl-3 pr-8 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
            >
              <option value="">{t('common.na')}</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.section_name || `Section ${section.id}`}
                  {section.lecturer && ` - ${section.lecturer}`}
                </option>
              ))}
            </select>
            {!selectedEvent && (
              <p className="text-sm text-gray-500 mt-1">{t('checkIn.messages.selectEventFirst')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.worshipId')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.mobilePhone')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.memberName')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.event')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.section')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.qrTime')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.checkInTime')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('attendance.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {attendanceRecords.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  {searchTerm || filters.event_id || filters.section_id 
                    ? t('attendance.noRecords')
                    : t('attendance.noRecords')
                  }
                </td>
              </tr>
            ) : (
              attendanceRecords.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {(record.worship_id !== null && record.worship_id !== undefined && record.worship_id !== '') ? record.worship_id : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {record.mobile || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {record.member_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="max-w-xs truncate">{record.event_name || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="max-w-xs truncate">{record.section_name || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="font-mono">{formatQRDateTime(record.current_datetime)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(record.check_in_time)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditAttendance(record)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors"
                      >
                        {t('attendance.actions.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteAttendance(record)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs transition-colors"
                      >
                        {t('attendance.actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalRecords > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('attendance.pagination.showing', { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, totalRecords), total: totalRecords })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('attendance.pagination.previous')}
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === currentPage - 2 ||
                      pageNum === currentPage + 2
                    ) {
                      return (
                        <span key={pageNum} className="px-2 py-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('attendance.pagination.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showAttendanceModal && (
        <AttendanceModal
          attendance={selectedAttendance}
          isCreateMode={isCreateMode}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

// Export with security wrapper requiring attendance access
// TEMPORARILY DISABLED FOR DEBUGGING
export default AttendancePage;
// export default requireAttendanceAccess(AttendancePage);
